import AlertCompanyDatasourcesCoverage from "@/components/company/alert-company-datasources-coverage";
import AlertCompanyLoading from "@/components/company/alert-company-loading";
import AvatarCompany from "@/components/company/avatar-company";
import CompanyChat from "@/components/company/company-chat";
import CompanyDebugModal from "@/components/company/company-debug-modal";
import CompanyListingsSelector from "@/components/company/company-listings-selector";
import CompanyNote from "@/components/company/company-note";
import CompanyPageSkeleton from "@/components/company/company-page-skeleton";
import {
  CompanyBudgetSpentAlert,
  CompanyErrorMessage,
} from "@/components/company/company-run-alerts";
import CompanyTagSelector from "@/components/company/company-tag-selector";
import CompanyUserAssignedSelector from "@/components/company/company-user-assigned-selector";
import DialogUpdateCompanySources from "@/components/company/dialog-update-company-sources";
import CompanyIntegrationsSync from "@/components/integrations/company/company-integrations-sync";
import { Button } from "@/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CompanyTabsNavigation, type TabConfig } from "./company-tabs-navigation";
import { CompanyTabContent } from "./company/company-tab-context";
import { useCompanyPageState } from "@/hooks/useCompanyPageState";
import {
  hasFinancialData,
  hasNewsData,
  hasOthersData,
} from "@/lib/company-data-availability";
import { COMPANY_TAB_SECTIONS } from "@/lib/company-card-sections";
import {
  bottomEdgeReach,
  rightEdgeInset,
} from "@/lib/support-launcher-placement";
import { cn } from "@/lib/utils";
import { enrichmentLevelLabels } from "@/utils/enrichment";
import {
  ArrowRightIcon,
  CodeIcon,
  PanelLeftCloseIcon,
  XIcon,
  PanelRightCloseIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { RiRobot2Line } from "react-icons/ri";
import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import { useModalOverlay } from "@/stores/modal-overlay-store";
import { useSupportLauncherObstruction } from "@/stores/support-launcher-store";

/**
 * Where the chat toggle sits once the chat is docked, as an inset from the
 * right edge of the details column it floats in — the `left` it slides to.
 *
 * Named because the support bubble perches on that button and has to be told
 * where it is going to be: the button cannot answer that itself while it is
 * sliding (a transition reports the frame it is on, not its destination), but
 * the column it is laid out from can.
 */
const CHAT_TOGGLE_DOCKED_RIGHT_PX = 80;

// Narrowest the docked chat can be dragged to: the composer's row of buttons
// needs about 400 px plus the chat's own padding, and below it the chat's cards
// wrap to a few words a line and their 12 px text becomes hard to read.
const CHAT_PANEL_MIN_WIDTH_PX = 440;

// Widest the docked chat can be, as a share of the page.
const CHAT_PANEL_MAX_SHARE = 0.5;

const TABS: readonly TabConfig[] = [
  { value: "insights", hasIcon: true },
  { value: "employees", showsPeople: true },
  {
    value: "news",
    isAvailable: hasNewsData,
    disabledTooltipKey: "newsNoData",
    filledFrom: COMPANY_TAB_SECTIONS.news,
  },
  {
    value: "financials",
    isAvailable: hasFinancialData,
    disabledTooltipKey: "financialsNoData",
    filledFrom: COMPANY_TAB_SECTIONS.financials,
  },
  { value: "administrative" },
  { value: "similars" },
  {
    value: "others",
    adminOnly: true,
    isAvailable: hasOthersData,
    disabledTooltipKey: "othersNoData",
    filledFrom: COMPANY_TAB_SECTIONS.others,
  },
];

export default function CompanyPage() {
  const {
    t,
    company,
    progress,
    done,
    sections,
    budgetSpent,
    error,
    isLoadingReady,
    activeTab,
    defaultTab,
    chatMode,
    activeConversationId,
    inputExpanded,
    promptInput,
    initialMessage,
    promptInputRef,
    dialogEdit,
    dialogJson,
    sharedSavedNote,
    sharedSelectedTag,
    sharedAssignedUser,
    sharedIntegrationSync,
    refResizableChat,
    scrollContainerRef,
    headerRef,
    isHeaderVisible,
    showActions,
    isAdminMode,
    isStreamInProgress,
    hasConnectedCrm,
    setChatMode,
    setInputExpanded,
    setPromptInput,
    setInitialMessage,
    setDialogEdit,
    setDialogJson,
    setSharedSavedNote,
    setSharedSelectedTag,
    setSharedAssignedUser,
    setSharedIntegrationSync,
    fetchCompanyData,
    handleTabChange,
    handleTabIntent,
    handleTabIntentEnd,
    handlePromptSubmit,
    handleConversationChange,
    handleMoveToSidePanel,
  } = useCompanyPageState();
  // Stable: the tab content and each panel are memoised on their props.
  const openSourcesEdit = useCallback(() => setDialogEdit(true), [setDialogEdit]);

  // Lock the chat content to its final width during the slide-in animation
  // so that the inner layout doesn't reflow as the panel grows.
  const panelGroupRef = useRef<HTMLDivElement>(null);
  const [isSidePanelOpening, setIsSidePanelOpening] = useState(false);
  const [lockedChatWidth, setLockedChatWidth] = useState(0);

  // The company info side panel opens as a non-modal sheet on the right. When
  // the chat is shown as a centered modal, slide it left so the panel doesn't
  // cover it; it slides back to center once the panel closes.
  const sidePanelState = useSyncExternalStore(
    companyPreviewPanelStore.subscribe,
    companyPreviewPanelStore.getSnapshot,
  );
  const isSidePanelDocked =
    sidePanelState !== null && !sidePanelState.modal;

  // The modal chat draws its own backdrop rather than mounting a `Dialog`, so
  // it registers the overlay itself — otherwise the support bubble, which
  // floats above every layer the app can draw, would stay on top of it.
  useModalOverlay(chatMode === "modal");

  // The support bubble rests in the viewport's bottom-right corner everywhere
  // else; while the chat is docked it perches on the toggle that opened it,
  // centred over the button at the seam between the company and its chat.
  const [detailsColumn, setDetailsColumn] = useState<HTMLDivElement | null>(
    null,
  );
  const [chatToggle, setChatToggle] = useState<HTMLButtonElement | null>(null);
  const [chatToggleReach, setChatToggleReach] = useState(0);
  const [chatToggleCentreRight, setChatToggleCentreRight] = useState<
    number | null
  >(null);

  useLayoutEffect(() => {
    if (!chatToggle || !detailsColumn || chatMode !== "sidepanel") {
      setChatToggleReach(0);
      setChatToggleCentreRight(null);
      return;
    }
    const measure = () => {
      setChatToggleReach(bottomEdgeReach(chatToggle));
      const columnRight = rightEdgeInset(detailsColumn);
      // `offsetWidth`, like `bottomEdgeReach`, so the 5% the button grows under
      // the pointer — where the pointer is, by definition, on the click that
      // docks the chat — doesn't shift the bubble off its centre.
      setChatToggleCentreRight(
        columnRight === null
          ? null
          : Math.round(
              columnRight +
                CHAT_TOGGLE_DOCKED_RIGHT_PX -
                chatToggle.offsetWidth / 2,
            ),
      );
    };
    measure();
    // The column is what carries the button: it is laid out from the column's
    // right edge, and that edge travels on every frame of the panel's slide and
    // of a handle drag — which the button's own box cannot be asked about while
    // it is still sliding there. The window listener covers a viewport resize,
    // where the edge moves without the launcher's corner moving with it.
    const observer = new ResizeObserver(measure);
    observer.observe(detailsColumn);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [chatToggle, detailsColumn, chatMode]);

  useSupportLauncherObstruction(chatToggleReach, chatToggleCentreRight);

  useEffect(() => {
    if (chatMode !== "sidepanel") {
      setIsSidePanelOpening(false);
      return;
    }
    const groupWidth = panelGroupRef.current?.offsetWidth ?? 0;
    // The panel opens at 30 %, never below its minimum nor above its 50 %
    // maximum — the bound the panel library itself applies last, so on a
    // window too narrow for both, the opening ends where the panel settles.
    setLockedChatWidth(
      Math.min(
        Math.max(groupWidth * 0.3, CHAT_PANEL_MIN_WIDTH_PX),
        groupWidth * CHAT_PANEL_MAX_SHARE,
      ),
    );
    setIsSidePanelOpening(true);
    const timeout = setTimeout(() => setIsSidePanelOpening(false), 300);
    return () => clearTimeout(timeout);
  }, [chatMode]);

  // Any change of chat presentation — opening, closing, or moving between modal
  // and side panel — dismisses the company info panel opened from its mentions.
  // It belongs to the chat, so it shouldn't linger over the page once the chat
  // is gone, nor stay on a surface (sheet vs inline) that no longer matches the
  // new mode.
  useEffect(() => {
    if (companyPreviewPanelStore.getSnapshot()?.modal === false) {
      companyPreviewPanelStore.close();
    }
  }, [chatMode]);

  if (isLoadingReady) {
    return (
      <div className="container">
        <div className="px-4 py-4 md:px-8 md:py-6">
          <AlertCompanyLoading
            progress={progress}
            className="mb-2"
          />
          <CompanyPageSkeleton />
        </div>
      </div>
    );
  }

  // An error after the stored company was painted keeps the page (the toast
  // reports it); the error screen is for a company that never arrived.
  if (!company) {
    return <CompanyErrorMessage error={error} className="mt-10" />;
  }

  return (
    <>
      <div ref={panelGroupRef} className="w-full h-full">
      <ResizablePanelGroup orientation="horizontal" className="w-full">
        <ResizablePanel
          defaultSize="50%"
          className="relative"
          elementRef={setDetailsColumn}
        >
          <div
            ref={scrollContainerRef}
            className="absolute top-0 left-0 flex-1 w-full h-full px-4 overflow-auto md:px-8 @container"
          >
            {/* Sticky compact header */}
            {!isHeaderVisible && (
              <div className="sticky top-0 z-10 -mx-4 duration-300 border-b md:-mx-8 bg-background/95 backdrop-blur-sm border-border animate-in fade-in slide-in-from-top-2">
                {/* Wide layout: single row */}
                <div className="hidden @[1270px]:flex items-center justify-between gap-6 px-4 py-3 md:px-8">
                  <div className="flex items-center min-w-0 gap-4">
                    <AvatarCompany className="w-10 h-10" company={company} />
                    <div className="min-w-0">
                      <h3 className="text-xl font-semibold truncate">
                        {company.name}
                      </h3>
                    </div>
                  </div>

                  <CompanyTabsNavigation
                    tabs={TABS}
                    activeTab={activeTab}
                    defaultTab={defaultTab}
                    onTabChange={handleTabChange}
                    onTabIntent={handleTabIntent}
                    onTabIntentEnd={handleTabIntentEnd}
                    company={company}
                    sections={sections}
                    isAdminMode={isAdminMode}
                    compact
                  />

                  <div className="flex items-center gap-1.5 shrink-0">
                    <AlertCompanyDatasourcesCoverage
                      company={company}
                      triggerDialogSourcesEdit={openSourcesEdit}
                    />
                    {showActions && (
                      <>
                        <CompanyTagSelector
                          company={company}
                          selectedTag={sharedSelectedTag}
                          onSelectedTagChange={setSharedSelectedTag}
                          compact
                        />
                        <CompanyListingsSelector company={company} compact />
                        <CompanyNote
                          company={company}
                          savedNote={sharedSavedNote}
                          onSavedNoteChange={setSharedSavedNote}
                          compact
                        />
                        <CompanyUserAssignedSelector company={company} selectedUser={sharedAssignedUser} onSelectedUserChange={setSharedAssignedUser} />
                        {hasConnectedCrm && (
                          <CompanyIntegrationsSync company={company} syncState={sharedIntegrationSync} onSyncStateChange={setSharedIntegrationSync} />
                        )}
                        {isAdminMode && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="w-10 h-10 border-warning"
                                onClick={() => setDialogJson(true)}
                              >
                                <CodeIcon className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {company.enrichmentLevel} -{" "}
                              {enrichmentLevelLabels[company.enrichmentLevel]}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Narrow layout: stacked rows */}
                <div className="flex flex-col items-center gap-2 px-4 py-3 md:px-8 @[1270px]:hidden">
                  <div className="flex items-center gap-3">
                    <AvatarCompany className="w-8 h-8" company={company} />
                    <div className="min-w-0 flex flex-col items-start">
                      <h3 className="text-lg font-semibold truncate">
                        {company.name}
                      </h3>
                    </div>
                  </div>

                  <CompanyTabsNavigation
                    tabs={TABS}
                    activeTab={activeTab}
                    defaultTab={defaultTab}
                    onTabChange={handleTabChange}
                    onTabIntent={handleTabIntent}
                    onTabIntentEnd={handleTabIntentEnd}
                    company={company}
                    sections={sections}
                    isAdminMode={isAdminMode}
                    compact
                  />

                  <div className="flex items-center gap-1.5">
                    <AlertCompanyDatasourcesCoverage
                      company={company}
                      triggerDialogSourcesEdit={openSourcesEdit}
                    />
                    {showActions && (
                      <>
                        <CompanyTagSelector
                          company={company}
                          selectedTag={sharedSelectedTag}
                          onSelectedTagChange={setSharedSelectedTag}
                          compact
                        />
                        <CompanyListingsSelector company={company} compact />
                        <CompanyNote
                          company={company}
                          savedNote={sharedSavedNote}
                          onSavedNoteChange={setSharedSavedNote}
                          compact
                        />
                        <CompanyUserAssignedSelector company={company} selectedUser={sharedAssignedUser} onSelectedUserChange={setSharedAssignedUser} />
                        {hasConnectedCrm && (
                          <CompanyIntegrationsSync company={company} syncState={sharedIntegrationSync} onSyncStateChange={setSharedIntegrationSync} />
                        )}
                        {isAdminMode && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="w-10 h-10 border-warning"
                                onClick={() => setDialogJson(true)}
                              >
                                <CodeIcon className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {company.enrichmentLevel} -{" "}
                              {enrichmentLevelLabels[company.enrichmentLevel]}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="max-w-[1500px] mx-auto pb-[120px]">
              {/* Progress bar */}
              {!done && (
                <div className="pt-4 md:pt-6">
                  <AlertCompanyLoading
                    progress={progress}
                    className="mb-2"
                  />
                </div>
              )}

              {budgetSpent && (
                <div className="pt-4 md:pt-6">
                  <CompanyBudgetSpentAlert />
                </div>
              )}

              {/* Header */}
              <div ref={headerRef} className="pt-5 pb-3 md:pt-8 md:pb-4">
                <div className="flex flex-col @[1000px]:flex-row @[1000px]:items-center @[1000px]:justify-between gap-4">
                  {/* Left: avatar + name */}
                  <div className="flex items-center min-w-0 gap-3">
                    <div className="relative shrink-0">
                      <AvatarCompany className="w-24 h-24" company={company} />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-3xl font-bold leading-tight tracking-tight">
                        {company.name}
                      </h2>
                    </div>
                  </div>

                  {/* Right: action buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap @[1000px]:shrink-0">
                    <AlertCompanyDatasourcesCoverage
                      company={company}
                      triggerDialogSourcesEdit={openSourcesEdit}
                    />
                    {showActions && (
                      <>
                        <Separator
                          orientation="vertical"
                          className="mx-0.5 !h-5"
                        />
                        <CompanyTagSelector
                          company={company}
                          selectedTag={sharedSelectedTag}
                          onSelectedTagChange={setSharedSelectedTag}
                        />
                        <CompanyListingsSelector company={company} />
                        <CompanyNote
                          company={company}
                          savedNote={sharedSavedNote}
                          onSavedNoteChange={setSharedSavedNote}
                        />
                        <Separator
                          orientation="vertical"
                          className="mx-0.5 !h-5"
                        />
                        <CompanyUserAssignedSelector company={company} selectedUser={sharedAssignedUser} onSelectedUserChange={setSharedAssignedUser} />
                        {hasConnectedCrm && (
                          <CompanyIntegrationsSync company={company} syncState={sharedIntegrationSync} onSyncStateChange={setSharedIntegrationSync} />
                        )}
                        {isAdminMode && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="w-10 h-10 border-warning"
                                onClick={() => setDialogJson(true)}
                              >
                                <CodeIcon className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {company.enrichmentLevel} -{" "}
                              {enrichmentLevelLabels[company.enrichmentLevel]}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {isAdminMode && company && (
                <CompanyDebugModal
                  company={company}
                  open={dialogJson}
                  onOpenChange={setDialogJson}
                />
              )}

              {/* Tab bar */}
              <div className="flex justify-center">
                <CompanyTabsNavigation
                  tabs={TABS}
                  activeTab={activeTab}
                  defaultTab={defaultTab}
                  onTabChange={handleTabChange}
                  onTabIntent={handleTabIntent}
                  onTabIntentEnd={handleTabIntentEnd}
                  company={company}
                  sections={sections}
                  isAdminMode={isAdminMode}
                  className="mb-3"
                />
              </div>

              {/* Chat button / prompt input */}
              <div
                className={cn(
                  "fixed z-10 left-1/2 -translate-x-1/2",
                  "inline-flex flex-col items-center gap-2",
                  "bottom-5 sm:bottom-8",
                  "transition-[opacity,filter] duration-300 ease-in-out",
                  chatMode === null
                    ? "opacity-100 blur-0"
                    : "opacity-0 blur-md pointer-events-none",
                )}
              >
                <div
                  className={cn(
                    "relative inline-flex overflow-hidden rounded-full p-[3px]",
                    "transition-[width,box-shadow] duration-500 ease-in-out",
                    inputExpanded
                      ? "w-[min(600px,90vw)] shadow-xl shadow-purple-500/30"
                      : "w-[280px] sm:w-[300px] hover:shadow-xl hover:shadow-purple-500/30",
                  )}
                >
                  <span
                    className={cn(
                      "absolute inset-[-1000%] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]",
                      !done &&
                        "animate-[spin_2s_linear_infinite]",
                    )}
                  />
                  <div
                    className={cn(
                      "relative flex items-center w-full h-14 whitespace-nowrap",
                      "rounded-full px-4 gap-3",
                      "bg-white dark:bg-gray-900 text-foreground",
                      !inputExpanded && "cursor-pointer",
                    )}
                    onClick={() => {
                      if (activeConversationId) {
                        setChatMode("modal");
                        return;
                      }
                      if (!inputExpanded) {
                        setInputExpanded(true);
                      }
                    }}
                  >
                    <div className="relative flex items-center justify-center w-7 h-7 shrink-0">
                      <RiRobot2Line
                        className={cn(
                          "absolute text-xl sm:text-2xl transition-opacity duration-300",
                          activeConversationId ? "opacity-0" : "opacity-100",
                        )}
                      />
                      <span
                        className={cn(
                          "absolute inset-0 flex items-center justify-center transition-opacity duration-300",
                          activeConversationId
                            ? "opacity-100"
                            : "opacity-0 pointer-events-none",
                        )}
                      >
                        <span className="flex items-center gap-1">
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className={cn(
                                "w-1 h-1 rounded-full bg-current",
                                isStreamInProgress &&
                                  "animate-[bounce-dot_0.6s_ease-in-out_infinite]",
                              )}
                              style={
                                isStreamInProgress
                                  ? { animationDelay: `${i * 150}ms` }
                                  : undefined
                              }
                            />
                          ))}
                        </span>
                      </span>
                    </div>
                    {inputExpanded ? (
                      <input
                        ref={promptInputRef}
                        type="text"
                        value={promptInput}
                        onChange={(e) => setPromptInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handlePromptSubmit();
                          }
                          if (e.key === "Escape") {
                            setInputExpanded(false);
                            setPromptInput("");
                          }
                        }}
                        onBlur={() => {
                          if (!promptInput.trim()) {
                            setInputExpanded(false);
                          }
                        }}
                        placeholder={t("company.chat.talkWithAI")}
                        className="flex-1 min-w-0 text-sm bg-transparent border-none outline-none sm:text-base text-foreground placeholder:text-muted-foreground"
                      />
                    ) : (
                      <span className="relative flex-1 font-medium text-sm sm:text-base">
                        <span
                          className={cn(
                            "flex items-center gap-2 transition-opacity duration-300",
                            activeConversationId ? "opacity-0" : "opacity-100",
                          )}
                        >
                          <span className="hidden sm:inline">
                            {t("company.chat.talkWithAI")}
                          </span>
                          <span className="sm:hidden">
                            {t("company.chat.talkWithAIShort")}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "absolute inset-0 flex items-center transition-opacity duration-300",
                            activeConversationId ? "opacity-100" : "opacity-0",
                          )}
                        >
                          {t("company.chat.continueWithAI")}
                        </span>
                      </span>
                    )}
                    <button
                      onClick={handlePromptSubmit}
                      disabled={!promptInput.trim()}
                      className={cn(
                        "shrink-0 rounded-full w-10 h-10 flex items-center justify-center",
                        "cursor-pointer border-none outline-none",
                        "bg-primary text-primary-foreground",
                        "transition-opacity duration-300",
                        inputExpanded
                          ? "opacity-100"
                          : "opacity-0 pointer-events-none",
                      )}
                    >
                      <ArrowRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tab content */}
              <div>
                <CompanyTabContent
                  key={company.id}
                  company={company}
                  sections={sections}
                  activeTab={activeTab}
                  onEditSources={openSourcesEdit}
                />
              </div>
            </div>
          </div>
          {/* Toggle chat panel button – slides between positions */}
          <button
            ref={setChatToggle}
            onClick={() =>
              setChatMode(chatMode === "sidepanel" ? null : "sidepanel")
            }
            style={{
              left:
                chatMode === "sidepanel"
                  ? `calc(100% - ${CHAT_TOGGLE_DOCKED_RIGHT_PX}px)`
                  : inputExpanded
                    ? "calc(50% + min(312px, 45vw + 12px))"
                    : "calc(50% + 162px)",
            }}
            className={cn(
              "absolute z-10 bottom-[calc(1.25rem+3px)] sm:bottom-[calc(2rem+3px)]",
              "w-14 h-14 rounded-full flex items-center justify-center",
              "cursor-pointer border-none outline-none",
              "bg-gray-200 dark:bg-gray-700 text-black dark:text-white",
              "shadow-lg hover:shadow-xl hover:scale-105",
              "transition-all duration-300 ease-in-out",
              chatMode === "modal" && "!opacity-0 pointer-events-none",
            )}
          >
            {chatMode === "sidepanel" ? (
              <PanelLeftCloseIcon className="w-6 h-6" />
            ) : (
              <PanelRightCloseIcon className="w-6 h-6" />
            )}
          </button>
        </ResizablePanel>
        <ResizableHandle
          withHandle
          className={cn(
            "transition-opacity duration-300 hover:bg-gray-500 active:bg-gray-200",
            chatMode === "sidepanel"
              ? "opacity-100"
              : "opacity-0 pointer-events-none",
          )}
        />
        <ResizablePanel
          defaultSize="0px"
          minSize={`${CHAT_PANEL_MIN_WIDTH_PX}px`}
          maxSize={`${CHAT_PANEL_MAX_SHARE * 100}%`}
          collapsible
          collapsedSize="0px"
          panelRef={refResizableChat}
          className="relative overflow-hidden"
        >
          {/* Single CompanyChat instance – survives modal↔sidepanel transitions */}
          {(chatMode !== null || activeConversationId !== null) && (
            <div
              className={cn(
                chatMode === "modal" &&
                  cn(
                    "fixed inset-0 z-50 flex items-center justify-center transition-[padding] ease-in-out",
                    // Reserve the panel's width on the right so the centered
                    // modal slides left, keeping it clear of the side panel;
                    // match the panel's own open/close easing and durations.
                    isSidePanelDocked
                      ? "duration-[400ms] sm:pr-(--company-panel-width)"
                      : "duration-[250ms]",
                  ),
                chatMode === "sidepanel" &&
                  isSidePanelOpening &&
                  "absolute top-0 right-0 h-full",
                chatMode === "sidepanel" &&
                  !isSidePanelOpening &&
                  "absolute inset-0",
                chatMode === null && "absolute inset-0 invisible",
              )}
              style={
                chatMode === "sidepanel" && isSidePanelOpening
                  ? { width: `${lockedChatWidth}px` }
                  : undefined
              }
            >
              {/* Modal overlay */}
              {chatMode === "modal" && (
                <div
                  className="absolute inset-0 bg-black/80"
                  onClick={() => setChatMode(null)}
                />
              )}
              {/* Content wrapper */}
              <div
                className={cn(
                  chatMode === "modal" &&
                    "relative z-10 w-full max-w-4xl h-[85vh] p-0 gap-0 flex flex-col sm:rounded-xl border bg-background shadow-lg",
                  chatMode !== "modal" && "relative w-full h-full",
                )}
              >
                {chatMode === "modal" && (
                  <button
                    onClick={() => setChatMode(null)}
                    className="absolute right-0 top-[-30px] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 text-white cursor-pointer"
                  >
                    <XIcon className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                )}
                <div
                  className={cn(
                    "relative flex-1 min-h-0 w-full h-full",
                    chatMode === "modal" && "overflow-hidden rounded-xl",
                  )}
                >
                  <CompanyChat
                    company={company}
                    isModal={chatMode === "modal"}
                    onClose={() => setChatMode(null)}
                    initialMessage={initialMessage}
                    onInitialMessageConsumed={() => setInitialMessage(null)}
                    onConversationChange={handleConversationChange}
                    onMoveToSidePanel={
                      chatMode === "modal" ? handleMoveToSidePanel : undefined
                    }
                    resumeConversationId={activeConversationId}
                    // Only the docked side-panel chat renders the company
                    // preview inline; the modal keeps the global sheet overlay.
                    panelSurface={
                      chatMode === "sidepanel" ? "inline" : "sheet"
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
      </div>

      {dialogEdit && (
        <DialogUpdateCompanySources
          company={company}
          onSaved={() => {
            fetchCompanyData();
            setDialogEdit(false);
          }}
          onClose={() => setDialogEdit(false)}
        />
      )}
    </>
  );
}
